import type { TFunction } from 'i18next'
import { sub } from 'date-fns'

import { Environment, getCountryCodeBookId, getDefaultAppLanguage, Market } from '@leanpay/utils'

// START COMMON CONSTANTS
const getMarketFromHostname = (): Market => {
    const hostname = window.location.hostname

    if (hostname.includes('.leanpay.ro') || hostname.includes('rou')) {
        return Market.ROM
    }

    if (hostname.includes('.leanpay.si') || hostname.includes('slo')) {
        return Market.SLO
    }

    if (hostname.includes('.leanpay.hu') || hostname.includes('hun')) {
        return Market.HUN
    }

    throw new Error(`[MARKET] Unknown hostname: ${hostname}`)
}
const isPrivateIp =
    /^(10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(
        window.location.hostname,
    )
const isRunningOnLocalhost =
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    isPrivateIp ||
    (window.location.hostname.includes('leanpay') && !window.location.hostname.includes('.'))

export const currentMarket = isRunningOnLocalhost
    ? (import.meta.env.VITE_MARKET as Market)
    : getMarketFromHostname()

export const isMarketSlo = currentMarket === Market.SLO
export const isMarketRom = currentMarket === Market.ROM
export const isMarketHun = currentMarket === Market.HUN

const getGtmId = () => {
    if (isMarketSlo) {
        return 'GTM-WKKBF5KC'
    }
    if (isMarketRom) {
        return 'GTM-NPG78W56'
    }
    if (isMarketHun) {
        return 'GTM-TK88QW62'
    }

    return ''
}

export const gtmId = getGtmId()

const getEnvironmentFromHostname = () => {
    const hostname = window.location.hostname

    if (isRunningOnLocalhost) {
        return Environment.LOCAL
    }

    if (hostname.includes('dev')) {
        return Environment.TILT
    }

    if (hostname.startsWith('test')) {
        return Environment.TEST
    }

    if (hostname.startsWith('stage')) {
        return Environment.STAGE
    }

    if (hostname.startsWith('new-vendor') || hostname.startsWith('vendor')) {
        return Environment.PROD
    }

    throw new Error(`[ENV] Unknown hostname: ${hostname}`)
}

export const currentEnvironment = getEnvironmentFromHostname()

export const isLocalEnvironment = currentEnvironment === Environment.LOCAL
export const isTestEnvironment = currentEnvironment === Environment.TEST
export const isStageEnvironment = currentEnvironment === Environment.STAGE
export const isProdEnvironment = currentEnvironment === Environment.PROD
export const isTiltEnvironment = currentEnvironment === Environment.TILT
export const isNonProdLiveEnvironment = !isProdEnvironment && !isLocalEnvironment

export const defaultAppLanguage = getDefaultAppLanguage(currentMarket)
export const countryCodebookId = getCountryCodeBookId(currentMarket)

// END COMMON CONSTANTS

export enum CacheKey {
    PqQrCode = 'PqQrCode',
    SecurityQuestions = 'SecurityQuestions',
    VendorData = 'VendorData',
    Time = 'Time',
    AdministrativeUnits = 'AdministrativeUnits',
    CustomerBaseInfo = 'CustomerBaseInfo',
    PaymentPlans = 'PaymentPlans',
    Invoice = 'Invoice',
    VendorUser = 'VendorUser',
    VendorUserRole = 'VendorUserRole',
    VendorUsers = 'VendorUsers',
    VerificationAgents = 'VerificationAgents',
    VerificationAgent = 'VerificationAgent',
    InvoiceFiles = 'InvoiceFiles',
    Transactions = 'Transactions',
    Invoices = 'Invoices',
    Transaction = 'Transaction',
    SessionInfo = 'SessionInfo',
    VendorRequestInfo = 'VendorRequestInfo',
    CustomOffers = 'CustomOffers',
    CustomOffer = 'CustomOffer',
    CurrencyInfo = 'CurrencyInfo',
    Country = 'Country',
    PostZip = 'PostZip',
    Settlement = 'Settlement',
    SettlementByPostZip = 'SettlementByPostZip',
    Streets = 'Streets',
    StreetsBySettlement = 'StreetsBySettlement',
}

export enum NewVendorUserError {
    VENDOR_USER_WITH_EMAIL_EXISTS = 'VENDOR_USER_WITH_EMAIL_EXIST',
    VENDOR_USER_WITH_PHONE_EXISTS = 'AGENT_WITH_PHONE_NUMBER_EXISTS',
}

export const getContactDetails = () => {
    if (isMarketSlo) {
        return {
            contact: {
                salespeople: {
                    email: 'partner@leanpay.si',
                    phoneNumber: '+386 64 260 845',
                    toneSelection: '2',
                    workingHours: {
                        from: '8:00',
                        to: '21:00',
                    },
                },
                customers: {
                    email: 'info@leanpay.si',
                    phoneNumber: '+386 1 235 09 98',
                    toneSelection: '1',
                    workingHours: {
                        from: '8:00',
                        to: '21:00',
                    },
                },
                address: 'Leanpay d.o.o., Dimičeva ulica 16, 1000 Ljubljana',
                web: {
                    link: 'https://www.leanpay.si',
                    label: 'www.leanpay.si',
                },
                links: {
                    verificationApp: 'https://verification.leanpay.si',
                    faq: 'https://help.leanpay.com/sl/knowledge',
                    apiDocumentation: 'https://docs.leanpay.com',
                    faqForSalespeople: 'https://help.leanpay.com/sl/knowledge/za-prodajalce',
                    faqForCustomers: 'https://help.leanpay.com/sl/knowledge/za-kupce',
                    manuals: 'https://docs.leanpay.com',
                },
            },
        }
    }

    if (isMarketHun) {
        return {
            contact: {
                salespeople: {
                    email: 'partner@leanpay.hu',
                    phoneNumber: '+36 1 701 2020',
                    toneSelection: '2',
                    workingHours: {
                        from: '9:00',
                        to: '17:00',
                    },
                },
                customers: {
                    email: 'info@leanpay.hu',
                    phoneNumber: '+36 1 701 2020',
                    toneSelection: '1',
                    workingHours: {
                        from: '9:00',
                        to: '17:00',
                    },
                },
                address: 'Leanpay Hungary Zrt. 1117 Budapest, Dombóvári út 9. 4A',
                web: {
                    link: 'https://www.leanpay.hu',
                    label: 'www.leanpay.hu',
                },
                links: {
                    verificationApp: 'http://localhost:4203',
                    faq: 'https://help.leanpay.com/hu/knowledge',
                    apiDocumentation: 'https://docs.leanpay.com',
                    faqForSalespeople: 'https://help.leanpay.com/hu/knowledge/partnereinknek',
                    faqForCustomers: 'https://help.leanpay.com/hu/knowledge/%C3%BCgyfeleinknek',
                    manuals: 'https://docs.leanpay.com',
                },
            },
        }
    }

    if (isMarketRom) {
        return {
            contact: {
                salespeople: {
                    email: 'parteneri@leanpay.ro',
                    phoneNumber: '03163 18800',
                    toneSelection: '2',
                    workingHours: {
                        from: '8:00',
                        to: '18:00',
                    },
                },
                customers: {
                    email: 'info@leanpay.ro',
                    phoneNumber: '03163 18800',
                    toneSelection: '1',
                    workingHours: {
                        from: '8:00',
                        to: '21:00',
                    },
                },
                address:
                    'Str.Costache Negri, Nr.1-5, Et.4, clădirea Opera Center, București, România',
                web: {
                    link: 'https://www.leanpay.ro',
                    label: 'www.leanpay.ro',
                },
                links: {
                    verificationApp: 'http://localhost:4203',
                    faq: 'https://help.leanpay.com/ro/knowledge',
                    apiDocumentation: 'https://docs.leanpay.com',
                    faqForSalespeople: 'https://help.leanpay.com/ro/knowledge/pentru-furnizori',
                    faqForCustomers: 'https://help.leanpay.com/ro/knowledge/pentru-clien%C8%9Bi',
                    manuals: 'https://docs.leanpay.com',
                },
            },
        }
    }
}

export const getVerificationAppUrl = () => {
    if (isProdEnvironment) {
        return 'https://verification.leanpay.si'
    }
    if (isTestEnvironment) {
        return 'https://test-verification.leanpay.si'
    }

    if (isStageEnvironment) {
        return 'https://stage-verification.leanpay.si'
    }

    if (isTiltEnvironment) {
        // Extract the subdomain from the current URL
        const currentHost = window.location.hostname // e.g., lazar-spasic-slo-dev-checkout.leanpay.app
        const subdomain = currentHost.split('.')[0] // lazar-spasic-slo-dev

        // Construct the verification app URL
        return `https://${subdomain}-verification.leanpay.app/`
    }

    //Fallback to local
    return 'http://localhost:4305'
}

export const getMinMaxPurchaseAmount = () => {
    switch (currentMarket) {
        case Market.SLO:
            return {
                min: 50,
                max: 12000,
            }
        case Market.HUN:
            return {
                min: 25000,
                max: 2000000,
            }
        case Market.ROM:
            return {
                min: 250,
                max: 25000,
            }
        default:
            return {
                min: 50,
                max: 5000,
            }
    }
}

export enum TransactionDeliveryStep {
    TransactionDeliveryQuestion,
    PartialDelivery,
    FullDelivery,
    ConfirmPartialDelivery,
    TransactionDeliveryError,
    TransactionDeliverySuccessPartial,
    TransactionDeliverySuccessFull,
}
export enum ChangePasswordStep {
    ChangePassword,
    ChangePasswordSuccess,
    ChangePasswordError,
}
export enum AddNewVendorUserStep {
    AddNewVendorUser,
    AddNewVendorUserSuccess,
    AddNewVendorUserError,
}

export enum AddNewVerificationAgentStep {
    AddNewVerificationAgent,
    AddNewVerificationAgentSuccess,
    AddNewVerificationAgentError,
}

export enum PasswordResetStep {
    PasswordReset,
    PasswordResetSuccess,
    PasswordResetError,
}

export enum CreateOfferStep {
    EnterMobilePhone,
    EnterOfferCreationData,
    EnterPersonalDetails,
    ConfirmCreateOffer,
    CreateOfferSuccess,
    BlackListedUserInfo,
    UnverifiedUserInfo,
    CancelCreateOffer,
    CreateOfferExpiryDocumentError,
    CreateOfferError,
}

export enum ChangeSecurityEmailStep {
    ChangeSecurityEmail,
    ChangeSecurityEmailSuccess,
}

export enum ChangeSecurityQuestionStep {
    ChangeSecurityQuestion,
    ChangeSecurityQuestionSuccess,
}

export enum ChangeSecurityPasswordStep {
    ChangeSecurityPassword,
    ChangeSecurityPasswordSuccess,
}

export enum ChangeSecretWordStep {
    ChangeSecretWord,
    ChangeSecretWordSuccess,
}

export enum ChangeApiUrlStep {
    ChangeApiUrl,
    ChangeApiUrlSuccess,
}

export enum ChangeContactInfoStep {
    ChangeContactInfo,
    ChangeContactInfoSuccess,
}

export const getCreateOfferProgress = (step: CreateOfferStep) => {
    switch (step) {
        case CreateOfferStep.EnterMobilePhone:
            return 25
        case CreateOfferStep.EnterOfferCreationData:
            return 50
        case CreateOfferStep.EnterPersonalDetails:
            return 70
        case CreateOfferStep.ConfirmCreateOffer:
            return 90
        case CreateOfferStep.CreateOfferSuccess:
            return 100
        case CreateOfferStep.BlackListedUserInfo:
            return 100
        case CreateOfferStep.UnverifiedUserInfo:
            return 100
        default:
            return 100
    }
}

export const getTransactionDeliveryProgress = (step: TransactionDeliveryStep) => {
    switch (step) {
        case TransactionDeliveryStep.TransactionDeliveryQuestion:
            return 20
        case TransactionDeliveryStep.FullDelivery:
            return 40
        case TransactionDeliveryStep.PartialDelivery:
            return 40
        case TransactionDeliveryStep.ConfirmPartialDelivery:
            return 60
        case TransactionDeliveryStep.TransactionDeliveryError:
            return 100
        case TransactionDeliveryStep.TransactionDeliverySuccessPartial:
            return 100
        case TransactionDeliveryStep.TransactionDeliverySuccessFull:
            return 100
        default:
            return 100
    }
}

export const getChangePasswordProgress = (step: ChangePasswordStep) => {
    switch (step) {
        case ChangePasswordStep.ChangePassword:
            return 40
        case ChangePasswordStep.ChangePasswordSuccess:
            return 100
        case ChangePasswordStep.ChangePasswordError:
            return 100
        default:
            return 100
    }
}
export const getAddNewVendorUserProgress = (step: AddNewVendorUserStep) => {
    switch (step) {
        case AddNewVendorUserStep.AddNewVendorUser:
            return 40
        case AddNewVendorUserStep.AddNewVendorUserSuccess:
            return 100
        case AddNewVendorUserStep.AddNewVendorUserError:
            return 100
        default:
            return 100
    }
}
export const getNewVerificationAgentProgress = (step: AddNewVerificationAgentStep) => {
    switch (step) {
        case AddNewVerificationAgentStep.AddNewVerificationAgent:
            return 40
        case AddNewVerificationAgentStep.AddNewVerificationAgentSuccess:
            return 100
        case AddNewVerificationAgentStep.AddNewVerificationAgentError:
            return 100
        default:
            return 100
    }
}

export const getPasswordResetProgress = (step: PasswordResetStep) => {
    switch (step) {
        case PasswordResetStep.PasswordReset:
            return 40
        case PasswordResetStep.PasswordResetSuccess:
            return 100
        case PasswordResetStep.PasswordResetError:
            return 100
        default:
            return 100
    }
}

export const getChangeSecurityEmailProgress = (step: ChangeSecurityEmailStep) => {
    switch (step) {
        case ChangeSecurityEmailStep.ChangeSecurityEmail:
            return 40
        case ChangeSecurityEmailStep.ChangeSecurityEmailSuccess:
            return 100
        default:
            return 100
    }
}

export const getChangeSecurityQuestionProgress = (step: ChangeSecurityQuestionStep) => {
    switch (step) {
        case ChangeSecurityQuestionStep.ChangeSecurityQuestion:
            return 40
        case ChangeSecurityQuestionStep.ChangeSecurityQuestionSuccess:
            return 100
        default:
            return 100
    }
}

export const getChangeSecurityPasswordProgress = (step: ChangeSecurityPasswordStep) => {
    switch (step) {
        case ChangeSecurityPasswordStep.ChangeSecurityPassword:
            return 40
        case ChangeSecurityPasswordStep.ChangeSecurityPasswordSuccess:
            return 100
        default:
            return 100
    }
}

export const getChangeSecretWordProgress = (step: ChangeSecretWordStep) => {
    switch (step) {
        case ChangeSecretWordStep.ChangeSecretWord:
            return 40
        case ChangeSecretWordStep.ChangeSecretWordSuccess:
            return 100
        default:
            return 100
    }
}

export const getChangeApiUrlProgress = (step: ChangeApiUrlStep) => {
    switch (step) {
        case ChangeApiUrlStep.ChangeApiUrl:
            return 40
        case ChangeApiUrlStep.ChangeApiUrlSuccess:
            return 100
        default:
            return 100
    }
}

export const getChangeContactInfoProgress = (step: ChangeContactInfoStep) => {
    switch (step) {
        case ChangeContactInfoStep.ChangeContactInfo:
            return 40
        case ChangeContactInfoStep.ChangeContactInfoSuccess:
            return 100
        default:
            return 100
    }
}

export const passwordValidationRegex = new RegExp(
    '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d.*)(?=.*\\W.*)[a-zA-Z0-9\\S]{8,30}$',
)

export enum CheckYourLimitCommunicationChannel {
    Phone = 'Phone',
    Email = 'Email',
    QrCode = 'QrCode',
}

export enum VendorUserRole {
    ROLE_ADMIN = 'ROLE_ADMIN',
    ROLE_SUPER_AGENT = 'ROLE_SUPER_AGENT',
    ROLE_AGENT = 'ROLE_AGENT',
    ROLE_OFFICER = 'ROLE_OFFICER',
}

export const DEFAULT_FROM_DATE = sub(new Date(), { months: 1 })
export const DEFAULT_TO_DATE = new Date()

export const getScreenNameByUrl = (url: string, t: TFunction) => {
    if (url.includes('create-offer')) {
        return t('createOffer.title')
    }
    if (url.includes('remote-offer')) {
        return t('remoteOffer.title')
    }
    if (url.includes('check-your-limit')) {
        return t('checkYourLimit.title')
    }
    if (url.includes('offers')) {
        return t('offers.title')
    }
    if (url.includes('transactions')) {
        return t('transactions.title')
    }
    if (url.includes('user-management')) {
        return t('userManagement.title')
    }
    if (url.includes('payment-and-invoices')) {
        return t('paymentAndInvoices.title')
    }
    return ''
}

const getTLD = () => {
    switch (currentMarket) {
        case Market.SLO:
            return 'sl'
        case Market.HUN:
            return 'hu'
        case Market.ROM:
            return 'ro'
        default:
            return 'sl'
    }
}

export const verificationAppUrl = getVerificationAppUrl()
export const FAQUrl = `https://help.leanpay.com/${getTLD()}/knowledge`
export const apiDocsUrl = 'https://docs.leanpay.com/'
